import React, { ReactElement, useState } from 'react';
import './SplitPanelUtil.scss';
import { Icon } from '@scuf/common';

/**
 * Generic component to use for any component that is just a wrapper for specific location placement
 */
const GenericSection: React.FunctionComponent<React.PropsWithChildren<
  unknown
>> = (props: React.PropsWithChildren<React.PropsWithChildren<unknown>>) => {
  return <>{props.children}</>;
};

/**
 * Table Section, for use in the left side panel
 */
const SplitPanelTable: React.FunctionComponent<React.PropsWithChildren<
  unknown
>> = (props: React.PropsWithChildren<React.PropsWithChildren<unknown>>) => {
  return <div className="table-partition">{props.children}</div>;
};

interface ISplitPanelPanelHeaderProps {
  onPanelClose?: () => void;
}

/**
 * Header section, for use in the right side panel
 */
const SplitPanelPanelHeader: React.FunctionComponent<React.PropsWithChildren<
  ISplitPanelPanelHeaderProps
>> = (props: React.PropsWithChildren<ISplitPanelPanelHeaderProps>) => {
  return (
    <div className="panel-header">
      <div className="panel-header-title">
        {getChildComponent(SplitPanelUtil.PanelHeaderTitle, props.children)}
      </div>
      <div className="panel-header-actionbar">
        {getChildComponent(SplitPanelUtil.PanelHeaderActionbar, props.children)}
      </div>
      <div>
        <Icon
          root="building"
          name="close"
          className="close-icon"
          onClick={props.onPanelClose}
        />
      </div>
    </div>
  );
};

const SplitPanelPanelHeaderTitle = GenericSection.bind(GenericSection);

const SplitPanelPanelHeaderActionbar = GenericSection.bind(GenericSection);

/**
 * Content section, for use in the right side panel
 */
// Binding generic section to each alias to make it distinct so getChildComponent can distinguish component type;
const SplitPanelContent = GenericSection.bind(GenericSection);

const SplitPanelContentMainSection = GenericSection.bind(GenericSection);

interface ISplitPanelContentMainProps {
  className?: string;
}
/**
 * Content section, for use in the right side panel
 */
const SplitPanelContentMain: React.FunctionComponent<ISplitPanelContentMainProps> = (
  props: React.PropsWithChildren<ISplitPanelContentMainProps>
) => {
  const style = props.className
    ? `panel-content-main ${props.className}`
    : 'panel-content-main';
  return (
    <div className={style}>
      <div className="panel-content-main-section">
        {getChildComponent(
          SplitPanelUtil.PanelContentMainSection,
          props.children
        )}
      </div>
    </div>
  );
};

/**
 * List item to be used inside <SplitPanelUtil.Content>
 * @param name - left hand side field as string
 * @param value - right hand side field as string
 * @param nameRenderer - left hand side field as JSX element, for custom elements
 * @param valueRenderer - right hand side field as JSX element, for custom elements
 */
interface ISplitPanelContentListItemProps {
  name?: string;
  value?: string;
  nameRenderer?: () => JSX.Element;
  valueRenderer?: () => JSX.Element;
}

const SplitPanelContentListItem: React.FunctionComponent<ISplitPanelContentListItemProps> = (
  props: ISplitPanelContentListItemProps
) => {
  return (
    <>
      <div className="panel-partition-content-list-item">
        <div className="panel-partition-content-list-item-name">
          {props.nameRenderer
            ? props.nameRenderer()
            : props.name
            ? props.name
            : '-'}
        </div>
        <div className="panel-partition-content-list-item-value">
          {props.valueRenderer
            ? props.valueRenderer()
            : props.value
            ? props.value
            : '-'}
        </div>
      </div>
    </>
  );
};

/**
 * Content section, for use in the right side panel
 */
const SplitPanelFooter = GenericSection.bind(GenericSection);

interface ISplitPanelPanelProps {
  onOverlayClicked?: () => void;
}

const SplitPanelPanel: React.FunctionComponent<ISplitPanelPanelProps> = (
  props: React.PropsWithChildren<ISplitPanelPanelProps>
) => {
  return (
    <>
      <div
        className="panel-partition-overlay"
        onClick={() => {
          props.onOverlayClicked && props.onOverlayClicked();
        }}
      />
      <div className="panel-partition">
        <div className="panel-partition-control-bar">
          {getChildComponent(SplitPanelUtil.PanelHeader, props.children)}
        </div>
        <div className="panel-partition-content">
          {getChildComponent(SplitPanelUtil.PanelContent, props.children)}
        </div>
        <div className="panel-partition-footer-bar">
          {getChildComponent(SplitPanelUtil.PanelFooter, props.children)}
        </div> 
      </div>
    </>
  );
};

interface ISplitPanelUtilProps {
  panelSubheader?: string;
}

interface ISplitPanelChildren {
  /**
   * For the header of the right side panel. Text is automatically emboldened.
   */
  PanelHeader: typeof SplitPanelPanelHeader;
  PanelHeaderTitle: typeof SplitPanelPanelHeaderTitle;
  PanelHeaderActionbar: typeof SplitPanelPanelHeaderActionbar;
  /**
   * This section is set up to wrap a Forge UI DataTable.
   * To use it as intended, please include a <DataTable.HeaderBar> (or enable search, which adds this automatically) and <DataTable.Pagination>.
   * Styles will make the DataTable fit automatically.
   */
  Table: typeof SplitPanelTable;
  /**
   * For the lower content section of the right side panel. Populate with <SplitPanelUtil.Content.ListItem> elements
   */
  PanelContent: typeof SplitPanelContent;
  PanelContentMain: typeof SplitPanelContentMain;
  PanelContentMainSection: typeof SplitPanelContentMainSection;
  PanelListItem: typeof SplitPanelContentListItem;
  PanelFooter: typeof SplitPanelFooter;
  Panel: typeof SplitPanelPanel;
}

const getChildComponent = (
  Component: React.FunctionComponent,
  children?: React.ReactNode
) => {
  const result = React.Children.toArray(children).filter(
    (child: any) => child.type === Component
  );

  return result;
};

export const SplitPanelUtil: React.FunctionComponent<ISplitPanelUtilProps> &
  ISplitPanelChildren = (
  props: React.PropsWithChildren<ISplitPanelUtilProps>
) => {
  return <div className="split-panel-util">{props.children}</div>;
};

/**
 * Required, attaches the subcomponent to the function
 */
SplitPanelUtil.Panel = SplitPanelPanel;
SplitPanelUtil.PanelHeader = SplitPanelPanelHeader;
SplitPanelUtil.PanelHeaderTitle = SplitPanelPanelHeaderTitle;
SplitPanelUtil.PanelHeaderActionbar = SplitPanelPanelHeaderActionbar;
SplitPanelUtil.PanelContent = SplitPanelContent;
SplitPanelUtil.PanelContentMain = SplitPanelContentMain;
SplitPanelUtil.PanelContentMainSection = SplitPanelContentMainSection;
SplitPanelUtil.PanelFooter = SplitPanelFooter;
SplitPanelUtil.PanelListItem = SplitPanelContentListItem;
SplitPanelUtil.Table = SplitPanelTable;
